import { connect } from "redux-vue";
import PageTemplate from "./PageTemplate";
import Validator from "validatorjs";
import * as surveyActions from "../actions/survey";
import ApiError from "../components/ApiError";
import LoadingIndicator from "../components/LoadingIndicator";
import ValidatedComponent from "../components/ValidatedComponent";
import classNames from "classnames";

const SurveyPage = {
  props: ["survey", "fetchSurvey", "saveSurvey"],

  data() {
    return {
      formData: {
        capable: "",
        ways_used: "",
        measurable_improvement: "",
        anything_else: ""
      },
      formRules: {
        ways_used: "required",
        measurable_improvement: "required",
        anything_else: "required",
        capable: "required"
      },
      formErrors: null
    };
  },

  mounted() {
    this.fetchSurvey(this.$route.params.token);
  },

  methods: {
    /**
     *
     * @param event
     * @returns {Promise<void>}
     */
    async onSubmit(event) {
      event.preventDefault();
      const validation = new Validator(this.formData, this.formRules, {
        required: this.$t('required_field_message')
      });

      if (validation.passes()) {
        this.formErrors = null;
        await this.saveSurvey(this.$route.params.token, this.formData);
      } else {
        this.formErrors = validation.errors.all();
      }
    },
  },

  watch: {
    survey(nextVal, val) {
      if(val.fetch && nextVal.fetchSuccess) {
        this.$nextTick(() => {
          const $target = document.querySelector('html:not(.md) #radio-container');
          if($target) { // mobile: scroll radio group to center position
            $target.scrollLeft += ($target.scrollWidth / 4);
          }
        });
      }
    }
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    const loading = this.survey.fetch && <LoadingIndicator fill="black" class="mx-auto my-10"/>;

    const headerText = this.survey.saveSuccess
      ? this.$t('survey_page.thanks_message')
      : this.$t('survey_page.please_message');

    const headerTextClassname = classNames("text-sm lg:text-xl", this.survey.saveSuccess ? "text-green-500" : "text-white");

    const headerContent = this.survey.fetchSuccess && (
      <div class="bg-secondary">
        <div class="container lg:w-3/5 mx-auto px-3 lg:px-6 py-10 z-10 relative text-center">
          <h2 class="text-lg lg:text-2xl lg:text-3xl font-bold text-white">
            {this.$t('survey_page.h1', {
              name: this.survey.model.name,
              module: this.survey.model.module
            })}
          </h2>
          <h6 class={headerTextClassname}>
            {headerText}
          </h6>
        </div>
      </div>
    );

    const radioBtnColors = [];
    for (let i = 0xf0f0f0; i >= 0xa8a8a8; i -= 0x80808) {
      radioBtnColors.push("#" + i.toString(16));
    }

    const formDisable = this.survey.save || this.survey.saveSuccess;

    const pageContent = this.survey.fetchSuccess && !this.survey.saveSuccess && (
      <div class="bg-gray-300 overflow-x-hidden">
        <div class="py-5 lg:py-10 px-6 lg:px-0">
          <div class="lg:w-3/5 mx-auto">
            <form>
              <h3 class="text-base lg:text-2xl font-bold mb-5 lg:mb-10 text-blue-600">
                {this.$t('survey_page.h2')}
              </h3>
              <div class="bg-white -mx-6 lg:mx-0 lg:rounded">
                <div class="p-6 lg:p-12">
                  <p class="text-sm lg:text-base mb-12">
                    <span class="font-bold">
                      {this.$t('survey_page.line_1')}
                    </span>
                    <br/><br/>
                    {this.$t('survey_page.line_2')}
                    <br/><br/>
                    {this.$t('survey_page.line_3')}
                  </p>

                  <div class="mb-8">
                    <div class="text-sm lg:text-base mb-4">
                      <p class="font-bold">
                        {this.$t('survey_page.capable_label_line_1')}
                      </p>
                      <p>
                        {this.$t('survey_page.capable_label_line_2')}
                      </p>
                    </div>
                    <ValidatedComponent
                      name="capable"
                      errors={this.formErrors}
                      classname="border rounded"
                      inactiveClassname="border-white"
                      activeClassname="border-red-500 p-1 pb-0"
                      renderComponent={classname => (
                        <div class={classname}>
                          <ul class="flex items-center w-full justify-between text-xl font-bold overflow-x-auto" id="radio-container">
                            {radioBtnColors.map((color, i) => (
                              <li key={color}>
                                <label class="mr-4 lg:mr-0 block relative h-12 w-12">
                                  <input type="radio" name="survey-capable" value={i +1} class="absolute visible" v-model={this.formData.capable} disabled={formDisable}/>
                                  <span class="absolute cursor-pointer custom-radio-toggle bg-gray-100 text-white h-12 w-12 rounded focus:outline-none focus:shadow-outline text-center py-1 leading-loose" style={"background-color:" + color}>
                                    {i +1}
                                  </span>
                                </label>
                              </li>
                            ))}
                          </ul>
                          <div class="lg:hidden">
                            <div class="text-center text-xs text-gray-500 mt-3">
                              <i class="mdi mdi-gesture-swipe-horizontal text-base"></i>
                              {' '}{this.$t('survey_page.swipe_to_see_more')}
                            </div>
                          </div>
                          <div class="flex justify-between text-gray-700 py-2">
                            <p class="text-xs uppercase">
                              {this.$t('survey_page.less_capable')}
                            </p>
                            <p class="text-xs uppercase">
                              {this.$t('survey_page.most_capable')}
                            </p>
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  <div class="mb-8">
                    <label for="survey-ways-used" class="text-sm lg:text-base block mb-4">
                      <p class="font-bold">
                        {this.$t('survey_page.ways_used_line_1')}
                      </p>
                      <p>
                        {this.$t('survey_page.ways_used_line_2')}
                        <span class="text-blue-600">*</span>
                      </p>
                    </label>
                    <ValidatedComponent
                      name="ways_used"
                      errors={this.formErrors}
                      classname="bg-gray-200 text-sm appearance-none rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline border"
                      renderComponent={classname => (
                        <textarea placeholder={this.$t('survey_page.write_your_answer_here')} class={classname} v-model={this.formData.ways_used} id="survey-ways-used" rows="5">
                        </textarea>
                      )}
                    />
                  </div>

                  <div class="mb-8">
                    <label for="survey-measurable-improvement" class="text-sm lg:text-base block mb-4">
                      <i18n path="survey_page.measurable_improvement_label" tag="p" class="font-bold">
                        <span slot="measurable_way" class="text-blue-600">{this.$t('survey_page.measurable_way')}</span>
                      </i18n>
                    </label>
                    <ValidatedComponent
                      name="measurable_improvement"
                      errors={this.formErrors}
                      classname="bg-gray-200 text-sm appearance-none rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline border"
                      renderComponent={classname => (
                        <textarea placeholder={this.$t('survey_page.write_your_answer_here')} class={classname} v-model={this.formData.measurable_improvement} id="survey-measurable-improvement" rows="5">
                        </textarea>
                      )}
                    />
                  </div>

                  <div class="mb-8">
                    <label for="survey-anything-else" class="text-sm lg:text-base block mb-4">
                      <p class="font-bold">
                        {this.$t('survey_page.anything_else_label')}
                      </p>
                    </label>
                    <ValidatedComponent
                      name="anything_else"
                      errors={this.formErrors}
                      classname="bg-gray-200 text-sm appearance-none rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline border"
                      renderComponent={classname => (
                        <textarea placeholder={this.$t('survey_page.write_your_answer_here')} class={classname} v-model={this.formData.anything_else} id="survey-anything-else" rows="5">
                        </textarea>
                      )}
                    />
                  </div>

                  <p class="font-bold text-base lg:text-lg text-center lg:w-1/2 mx-auto my-10">
                    {this.$t('survey_page.final_thanks')}
                  </p>

                  <button
                    class="px-5 p-2 rounded-full bg-action hover:bg-action-dark text-white focus:outline-none block mx-auto"
                    type="submit" onClick={this.onSubmit} disabled={formDisable}>
                    {this.survey.save && <LoadingIndicator/>}
                    {this.survey.save || (
                      <fragment>
                        {this.$t('submit')}
                        <i class="mdi mdi-arrow-right ml-3"></i>
                      </fragment>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );

    const apiError = (
      <section class="container mx-auto px-3 lg:px-6">
        <ApiError error={this.survey.fetchError} class="mt-20 mb-20 text-center text-xl" />
      </section>
    );

    return (
      <PageTemplate>
        {loading}
        {headerContent}
        {pageContent}
        {apiError}
      </PageTemplate>
    );
  }
};

function mapStateToProps(state) {
  return {
    survey: state.survey
  };
}

function mapActionToProps(dispatch) {
  return {
    fetchSurvey: token => dispatch(surveyActions.fetchSurvey(token)),
    saveSurvey: async (token, data) =>
      await dispatch(surveyActions.saveSurvey(token, data))
  };
}

export default connect(mapStateToProps, mapActionToProps)(SurveyPage);
